import { useState, useEffect, useRef, forwardRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as math from 'mathjs';

import { Registry } from '../../FormBuilderLibrary';
import { stringToHTML } from '../utils';

import { TextField, FormHelperText, Tooltip, FormControl } from '@mui/material';

import { getElementIcon } from '../utils';

import StyledWrapper from './style';

const MUICalculator = forwardRef((props, ref) => {
	const { isFormBuilder, disabled } = props;
	const reduxFormAnswers = useSelector((state) => state?.form?.form);
	const [formulaPieces] = useState(props?.data?.calc_formula?.BE || []);
	const [allAnswers, setAllAnswers] = useState([]);
	const [totalValue, setTotalValue] = useState('');

	const operators = ['(', ')', '+', '-', '/', '*'];

	const getAllAnswers = () => {
		let answers = [];

		reduxFormAnswers?.forEach((answer) => {
			if (answer) {
				answers = [...answers, ...answer];
			}
		});

		setAllAnswers(answers);
	};

	const getValue = (field_name) => {
		const answer = allAnswers?.find((e) => e?.name === field_name);
		return answer?.value !== '' ? answer?.value : undefined;
	};

	const parsedLabel = useMemo(() => stringToHTML(props?.data?.label), [props?.data?.label]);
	const parsedHelperText = useMemo(
		() => stringToHTML(props?.data?.help_message),
		[props?.data?.help_message]
	);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const labelRef = useRef(null);
	const labelStyle = disabled ? { margin: '0px', color: 'rgba(0, 0, 0, 0.38)' } : { margin: '0px' };

	useEffect(() => {
		props.updateStore();
	}, [totalValue]);

	useEffect(() => {
		getAllAnswers();
	}, [reduxFormAnswers]);

	useEffect(() => {
		let formulaString = '';
		if (formulaPieces.length > 0) {
			formulaPieces.forEach((formulaPiece) => {
				const isOperatorPiece = operators.includes(formulaPiece);
				if (isOperatorPiece) {
					const operator = formulaPiece;
					formulaString += operator;
				} else {
					const formInputValue = getValue(formulaPiece?.replace(',', ''));
					formulaString += formInputValue;
				}
			});
			if (formulaString) {
				try {
					const evaluatedFormulaExpression = math.evaluate(formulaString).toLocaleString();
					setTotalValue(evaluatedFormulaExpression);
				} catch (e) {
					setTotalValue('');
				}
			} else {
				setTotalValue('');
			}
		}
	}, [allAnswers]);

	useEffect(() => {
		// ARM-397. The form elements initially take up the entire width of the screen and then are resized to fit into the proper row size. This setTimeout adds a delay so we do this calculation after the form elements are set to their proper row width. Without this the scroll width and clientWidth will be the same on screens with a width of 2000px and above
		setTimeout(() => {
			if (labelRef.current) {
				setIsOverflowing(labelRef.current.scrollWidth > labelRef.current.clientWidth);
			}
		}, 100);
	});

	return isFormBuilder ? (
		<StyledWrapper.FormControlNoBorder sx={{ width: '100%' }} variant="outlined">
			<Tooltip
				arrow
				placement="top"
				title={isOverflowing ? `${props?.data?.label}` : ''}
				enterTouchDelay={0}
			>
				<StyledWrapper.CustomLabel>
					<StyledWrapper.LabelContents ref={labelRef}>
						{isFormBuilder && getElementIcon(props.data?.key)}
						{parsedLabel?.[0] ? parsedLabel : props?.data?.label}
						{isFormBuilder && (
							<span>
								{' '}
								{props.data?.unique_identifier
									? `(ff-${props.data?.unique_identifier})`
									: `(tmp-${props.data?.temp_order})`}
							</span>
						)}
					</StyledWrapper.LabelContents>
				</StyledWrapper.CustomLabel>
			</Tooltip>
			<TextField
				error={props?.data?.error}
				name={name}
				inputRef={ref}
				disabled={disabled}
				style={{ width: '100%' }}
				multiline
				InputLabelProps={{ shrink: isFormBuilder || undefined }}
			/>
			<StyledWrapper.FormHelperTextContainer>
				<FormHelperText error={props?.data?.error}>
					{props?.data?.error
						? props?.data?.error?.error
						: parsedHelperText?.[0]
						? parsedHelperText
						: props?.data?.help_message}
				</FormHelperText>
			</StyledWrapper.FormHelperTextContainer>
		</StyledWrapper.FormControlNoBorder>
	) : (
		<FormControl fullWidth>
			<Tooltip
				arrow
				placement="top"
				title={isOverflowing ? `${props?.data?.label}` : ''}
				enterTouchDelay={0}
			>
				<StyledWrapper.CustomLabel>
					<StyledWrapper.LabelContents ref={labelRef} style={labelStyle}>
						{parsedLabel?.[0] ? parsedLabel : props?.data?.label}
					</StyledWrapper.LabelContents>
				</StyledWrapper.CustomLabel>
			</Tooltip>
			<StyledWrapper.BorderOutline
				disabled={disabled}
				id={`${props?.data?.label}`}
				style={{ minHeight: '56px', marginTop: '16px' }}
			>
				<span style={labelStyle} ref={ref}>
					{totalValue}
				</span>
			</StyledWrapper.BorderOutline>
			<StyledWrapper.FormHelperTextContainer>
				<FormHelperText error={props?.data?.error}>
					{props?.data?.error
						? props?.data?.error?.error
						: parsedHelperText?.[0]
						? parsedHelperText
						: props?.data?.help_message}
				</FormHelperText>
			</StyledWrapper.FormHelperTextContainer>
		</FormControl>
	);
});

MUICalculator.displayName = 'MUI_Calculator';
Registry.register('MUI_Calculator', MUICalculator);

export default MUICalculator;
